// apps/web/src/components/onboarding/OnboardingFlow.tsx
// Onboarding flow component

import { useState } from 'react';

export function OnboardingFlow() {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const steps = [
    { id: 'welcome', title: 'Welcome', description: 'Get started with BumBum Platform' },
    { id: 'company', title: 'Company Info', description: 'Tell us about your organization' },
    { id: 'team', title: 'Team Setup', description: 'Add your team members' },
    { id: 'permissions', title: 'Permissions', description: 'Set up access controls' },
    { id: 'integrations', title: 'Integrations', description: 'Connect your tools' },
    { id: 'campaign', title: 'First Campaign', description: 'Create your first campaign' },
    { id: 'creator', title: 'Add Creator', description: 'Add your first creator' },
    { id: 'complete', title: 'Complete', description: 'You\'re all set!' },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCompletedSteps([...completedSteps, currentStep]);
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(steps.length - 1);
    }
  };

  const handleComplete = () => {
    setCompletedSteps([...completedSteps, currentStep]);
    // Redirect or complete onboarding
    console.log('Onboarding completed!');
  };

  const renderStepContent = () => {
    const step = steps[currentStep]!;
    switch (step.id) {
      case 'welcome':
        return (
          <div className="onboarding-step">
            <h2>Welcome to BumBum Platform</h2>
            <p className="welcome-text">
              We&apos;re excited to have you on board! This quick walkthrough will help you get familiar with our platform.
            </p>
            <div className="welcome-features">
              <div className="feature-card">
                <h3>📊 Campaign Management</h3>
                <p>Create and manage your marketing campaigns</p>
              </div>
              <div className="feature-card">
                <h3>👥 Creator Collaboration</h3>
                <p>Work seamlessly with creators</p>
              </div>
              <div className="feature-card">
                <h3>💰 Financial Tracking</h3>
                <p>Manage invoices and payments</p>
              </div>
            </div>
          </div>
        );
      case 'company':
        return (
          <div className="onboarding-step">
            <h2>Company Information</h2>
            <p>Let&apos;s start by learning about your organization.</p>
            <form className="company-form">
              <input type="text" placeholder="Company Name" required />
              <input type="text" placeholder="Industry" required />
              <input type="url" placeholder="Company Website" />
              <textarea placeholder="Company Description"></textarea>
            </form>
          </div>
        );
      case 'team':
        return (
          <div className="onboarding-step">
            <h2>Team Setup</h2>
            <p>Add your team members to get started.</p>
            <div className="team-members">
              <div className="member-input">
                <input type="email" placeholder="Team Member Email" />
                <select>
                  <option>Admin</option>
                  <option>Manager</option>
                  <option>Editor</option>
                  <option>Viewer</option>
                </select>
              </div>
              <button className="btn btn-outline">Add Another Member</button>
            </div>
          </div>
        );
      case 'permissions':
        return (
          <div className="onboarding-step">
            <h2>Permission Settings</h2>
            <p>Define access levels for your team members.</p>
            <div className="permissions-grid">
              <div className="permission-item">
                <label>
                  <input type="checkbox" defaultChecked /> 
                  Campaign Management
                </label>
              </div>
              <div className="permission-item">
                <label>
                  <input type="checkbox" defaultChecked /> 
                  Creator Management
                </label>
              </div>
              <div className="permission-item">
                <label>
                  <input type="checkbox" defaultChecked /> 
                  Financial Management
                </label>
              </div>
              <div className="permission-item">
                <label>
                  <input type="checkbox" /> 
                  Admin Access
                </label>
              </div>
            </div>
          </div>
        );
      case 'integrations':
        return (
          <div className="onboarding-step">
            <h2>Integrations</h2>
            <p>Connect your favorite tools to streamline your workflow.</p>
            <div className="integrations-grid">
              <div className="integration-card">
                <h3>Google Analytics</h3>
                <p>Track campaign performance</p>
                <button className="btn btn-outline">Connect</button>
              </div>
              <div className="integration-card">
                <h3>Adobe Creative Suite</h3>
                <p>Access creative assets</p>
                <button className="btn btn-outline">Connect</button>
              </div>
              <div className="integration-card">
                <h3>Slack</h3>
                <p>Team collaboration</p>
                <button className="btn btn-outline">Connect</button>
              </div>
            </div>
          </div>
        );
      case 'campaign':
        return (
          <div className="onboarding-step">
            <h2>Create Your First Campaign</h2>
            <p>Start by creating your first marketing campaign.</p>
            <form className="campaign-form">
              <input type="text" placeholder="Campaign Name" required />
              <textarea placeholder="Campaign Description"></textarea>
              <input type="date" placeholder="Start Date" />
              <input type="date" placeholder="End Date" />
              <select required>
                <option value="">Select Campaign Type</option>
                <option value="social">Social Media</option>
                <option value="email">Email Marketing</option>
                <option value="video">Video Campaign</option>
                <option value="content">Content Marketing</option>
              </select>
            </form>
          </div>
        );
      case 'creator':
        return (
          <div className="onboarding-step">
            <h2>Add Your First Creator</h2>
            <p>Bring your creators into the platform.</p>
            <form className="creator-form">
              <input type="text" placeholder="Creator Name" required />
              <select required>
                <option value="">Select Creator Type</option>
                <option value="individual">Individual Creator</option>
                <option value="business">Creator Business</option>
                <option value="collective">Creator Collective</option>
                <option value="media">Media Creator</option>
              </select>
              <input type="email" placeholder="Creator Email" />
              <input type="tel" placeholder="Creator Phone" />
            </form>
          </div>
        );
      case 'complete':
        return (
          <div className="onboarding-step complete">
            <h2>Welcome Aboard!</h2>
            <p className="completion-text">
              You&apos;ve successfully completed the onboarding process. 
              You&apos;re now ready to start using BumBum Platform!
            </p>
            <div className="completion-features">
              <div className="feature-check">
                <span className="checkmark">✓</span>
                <span>Company setup complete</span>
              </div>
              <div className="feature-check">
                <span className="checkmark">✓</span>
                <span>Team members added</span>
              </div>
              <div className="feature-check">
                <span className="checkmark">✓</span>
                <span>Campaign created</span>
              </div>
              <div className="feature-check">
                <span className="checkmark">✓</span>
                <span>First creator added</span>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="onboarding-step">
            <h2>Onboarding Step</h2>
            <p>Step content for {step.title}</p>
          </div>
        );
    }
  };

  return (
    <div className="onboarding-flow">
      <div className="onboarding-header">
        <h1>BumBum Platform Onboarding</h1>
        <p>Get started with our platform in just a few steps</p>
      </div>

      <div className="onboarding-progress">
        <div className="progress-steps">
          {steps.map((step, index) => (
            <div 
              key={step.id} 
              className={`step ${index <= currentStep ? 'completed' : ''} ${index === currentStep ? 'active' : ''}`}
            >
              <div className="step-number">{index + 1}</div>
              <div className="step-label">{step.title}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="onboarding-content">
        {renderStepContent()}
      </div>

      <div className="onboarding-navigation">
        <button 
          className="btn btn-secondary"
          onClick={handlePrevious}
          disabled={currentStep === 0}
        >
          Previous
        </button>
        
        {currentStep === steps.length - 1 ? (
          <button 
            className="btn btn-primary"
            onClick={handleComplete}
          >
            Complete Onboarding
          </button>
        ) : currentStep === steps.length - 2 ? (
          <button 
            className="btn btn-primary"
            onClick={handleNext}
          >
            Finish Setup
          </button>
        ) : (
          <button 
            className="btn btn-primary"
            onClick={handleNext}
          >
            Next
          </button>
        )}
        
        {currentStep < steps.length - 2 && (
          <button 
            className="btn btn-link"
            onClick={handleSkip}
          >
            Skip
          </button>
        )}
      </div>
    </div>
  );
}